/**
 * Table-centric pages — premium minimal skeleton (admin lists, broker dashboards).
 */

import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@hooks/useLanguage';
import {
  SkeletonBreath,
  SkeletonCircle,
  SkeletonRect,
  SkeletonText,
} from './skeletonPrimitives';

export interface TablePageSkeletonProps {
  className?: string;
  summaryCards?: 0 | 2 | 3;
  showPageHeading?: boolean;
  showPrimaryAction?: boolean;
  showSecondaryAction?: boolean;
  showSearch?: boolean;
  showContentCard?: boolean;
  showSectionHeading?: boolean;
  filterSelectCount?: number;
  tableRows?: number;
  tableColumns?: number;
  showTablePager?: boolean;
  tableWidth?: 'md' | 'lg';
}

const cardShell = 'rounded-card border border-slate-200/50 bg-white';

export function TablePageSkeleton({
  className = '',
  summaryCards = 0,
  showPageHeading = false,
  showPrimaryAction = false,
  showSecondaryAction = false,
  showSearch = false,
  showSectionHeading = false,
  showContentCard = false,
  filterSelectCount = 0,
  tableRows = 8,
  tableColumns = 6,
  showTablePager = true,
  tableWidth = 'lg',
}: TablePageSkeletonProps) {
  const { t } = useTranslation();
  const { isRtl } = useLanguage();

  const showTopHeader = showPageHeading || showPrimaryAction || showSecondaryAction;

  const cardGridClass =
    summaryCards === 3
      ? 'mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3'
      : summaryCards === 2
        ? 'mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2'
        : '';

  return (
    <div
      className={clsx('font-sans animate-pulse', className)}
      dir={isRtl ? 'rtl' : 'ltr'}
      aria-busy="true"
      aria-live="polite"
      aria-label={t('common.loading', { defaultValue: 'Loading…' })}
    >
      <SkeletonBreath>
        {showTopHeader && (
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              {showPageHeading && (
                <>
                  <SkeletonText className="w-[min(55%,280px)]" />
                  <SkeletonText className="mt-2 w-[min(85%,400px)] max-w-full" />
                </>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-3 sm:ms-auto">
              {showSecondaryAction && <SkeletonRect className="h-11 w-[7.5rem]" />}
              {showPrimaryAction && <SkeletonRect className="h-12 w-[10.5rem]" />}
            </div>
          </div>
        )}

        {summaryCards > 0 && (
          <section className={cardGridClass} aria-hidden>
            {Array.from({ length: summaryCards }, (_, i) => (
              <div key={i} className={clsx(cardShell, 'p-6 sm:p-8')}>
                <div className="flex items-center gap-3">
                  <SkeletonCircle className="size-10" />
                  <SkeletonText className="min-w-0 flex-1 max-w-[120px]" />
                </div>
                <SkeletonText className="mt-4 w-16 sm:w-20" />
                <SkeletonText className="mt-2 w-3/4 max-w-[200px]" />
              </div>
            ))}
          </section>
        )}

        {showSearch && (
          <div className="mb-4 max-w-md" aria-hidden>
            <SkeletonText className="mb-2 w-24" />
            <SkeletonRect className="h-[46px] w-full" />
          </div>
        )}

        {showSectionHeading && (
          <div className="mb-3" aria-hidden>
            <SkeletonText className="w-[min(70%,320px)]" />
            <SkeletonText className="mt-2 w-[min(90%,400px)] max-w-full" />
          </div>
        )}

        {showContentCard && (
          <section className={clsx(cardShell, 'mb-6 p-6 sm:p-8')} aria-hidden>
            <SkeletonText className="w-[min(75%,260px)]" />
            <SkeletonText className="mt-2 w-full max-w-md" />
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <SkeletonCircle className="size-10" />
              <div className="min-w-0 flex-1 space-y-2">
                <SkeletonText className="w-full max-w-xs" />
                <SkeletonText className="w-full max-w-sm" />
              </div>
              <SkeletonRect className="h-9 w-28" />
            </div>
          </section>
        )}

        <section className={clsx('overflow-hidden', cardShell)}>
          {filterSelectCount > 0 && (
            <div
              className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-4 sm:p-6"
              aria-hidden
            >
              {Array.from({ length: filterSelectCount }, (_, i) => (
                <SkeletonRect key={i} className="h-10 w-[min(100%,11rem)] sm:w-44" />
              ))}
            </div>
          )}
          <div className="overflow-x-auto" aria-hidden>
            <table
              className={clsx(
                'w-full border-collapse font-sans text-sm',
                tableWidth === 'md' ? 'min-w-[700px]' : 'min-w-[900px]',
              )}
            >
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  {Array.from({ length: tableColumns }, (_, i) => (
                    <th key={i} className="py-3 px-4 text-start">
                      <SkeletonText className="w-20" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: tableRows }, (_, row) => (
                  <tr
                    key={row}
                    className={clsx(
                      'border-b border-slate-100/80 last:border-0',
                      row % 2 === 1 ? 'bg-slate-50/50' : '',
                    )}
                  >
                    {Array.from({ length: tableColumns }, (_, col) => (
                      <td key={col} className="py-3 px-4">
                        <SkeletonText
                          className={
                            col === 0 ? 'w-8' : col === tableColumns - 1 ? 'w-16' : 'w-full max-w-[120px]'
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {showTablePager && (
            <div
              className="flex justify-center gap-2 border-t border-slate-100 p-4 sm:justify-end"
              aria-hidden
            >
              <SkeletonRect className="h-9 w-24" />
              <SkeletonRect className="h-9 w-24" />
            </div>
          )}
        </section>
      </SkeletonBreath>
    </div>
  );
}
